import { uuidv4, normalizeExtensionPattern } from '@/utils'
import { some } from 'lodash-es'
import { getDropboxFolderContent } from '@/utils/api/dataManagement'

function collectionParamFilt(dataset, collections, params) {
  let keepFile = true
  const collectionId = dataset.collectionId
  const collection = collections[collectionId]
  for (const [key, value] of Object.entries(params)) {
    if (!collection[key] === value) {
      keepFile = false
      break
    }
  }
  return keepFile
}

/**
 * Create initial expansion state for a dataset
 */
function createExpansionState(files) {
  const isExpandableFolder = files.length === 1 && files[0]?.is_dir
  return {
    isExpanded: false,
    isExpanding: false,
    expandable: isExpandableFolder,
    relativePath: isExpandableFolder ? files[0]?.relativePath : null,
    originalFiles: files, // Store original for rollback
    expandPattern: '',
  }
}

/**
 * Serialize dataset for API payload (exclude expansion metadata)
 */
export function serializeDatasetForPayload(dataset) {
  // eslint-disable-next-line no-unused-vars
  const { expansion, ...datasetWithoutExpansion } = dataset
  return datasetWithoutExpansion
}

/**
 * Serialize all datasets for API payload
 */
export function serializeDatasetsForPayload(datasets) {
  return Object.entries(datasets).reduce((acc, [id, dataset]) => {
    acc[id] = serializeDatasetForPayload(dataset)
    return acc
  }, {})
}

// const

const state = {
  datasets: {},
  extractedDatasets: {},
}

const getters = {
  datasetIds: (state) => {
    return Object.keys(state.datasets)
  },
  datasetsList: (state, getters) => {
    return getters.datasetIds.reduce((datasetsList, id) => {
      datasetsList.push({ id, ...state.datasets[id] })
      return datasetsList
    }, [])
  },
  hasStagedData: (state, getters) => {
    return getters.datasetsList.length > 0
  },
  rawDatasetsList: (state, getters, rootState) => {
    const collections = rootState.dataloader.collections.collections
    return getters.datasetsList.filter((dataset) =>
      collectionParamFilt(dataset, collections, { isRawData: true }),
    )
  },
  derivedDatasetsList: (state, getters, rootState) => {
    const collections = rootState.dataloader.collections.collections
    return getters.datasetsList.filter((dataset) =>
      collectionParamFilt(dataset, collections, { isRawData: false }),
    )
  },
  datasetsListsByCollectionId: (state, getters) => {
    const datasetsListsByCollectionId = {}
    getters.datasetsList.forEach((dataset) => {
      const collectionId = dataset.collectionId
      datasetsListsByCollectionId[collectionId] = datasetsListsByCollectionId[
        collectionId
      ]
        ? [...datasetsListsByCollectionId[collectionId], dataset]
        : [dataset]
    })
    return datasetsListsByCollectionId
  },
  datasetFileIds: (state, getters) => {
    return getters.datasetIds
      .map((dId) => state.datasets[dId]?.files?.map(({ id }) => id))
      .flat()
  },
  nonAssignedFileIds: (state, getters, rootState, rootGetters) => {
    const fileIds = rootGetters['dataloader/dropbox/fileIds']
    return fileIds.filter((fId) => !getters.datasetFileIds.includes(fId))
  },
  remainingFilesList: (state, getters, rootState) => {
    const selectedFiles = rootState.dataloader.dropbox.selectedFiles
    return getters.nonAssignedFileIds.reduce((filesList, fId) => {
      filesList.push({ id: fId, ...selectedFiles[fId] })
      return filesList
    }, [])
  },
  remainingFilesCount: (state, getters) => {
    return getters.remainingFilesList.length
  },
  hasStagedRawData: (state, getters) => {
    return getters.rawDatasetsList.length > 0
  },
  getCollectionDatasetsList: (state, getters) => (id) => {
    return getters.datasetsListsByCollectionId[id] || []
  },
  obsoleteDatasetIds: (state, getters, rootState) => {
    const selectedFiles = rootState.dataloader.dropbox.selectedFiles
    return Object.entries(state.datasets).reduce(
      (obsoleteIds, [datasetId, { files }]) => {
        const filesMissingStatus = files.map(({ id }) => !(id in selectedFiles))
        const someMissing = some(filesMissingStatus)
        if (someMissing) obsoleteIds.push(datasetId)
        return obsoleteIds
      },
      [],
    )
  },
  // Expansion-related getters
  expandableDatasets: (state) => {
    return Object.keys(state.datasets).filter((id) => {
      const dataset = state.datasets[id]
      return (
        dataset.expansion &&
        dataset.expansion.expandable &&
        !dataset.expansion.isExpanded
      )
    })
  },
  expandedDatasets: (state) => {
    return Object.keys(state.datasets).filter((id) => {
      const dataset = state.datasets[id]
      return dataset.expansion && dataset.expansion.isExpanded
    })
  },
  hasExpandableFolders: (state, getters) => {
    return getters.expandableDatasets.length > 0
  },
  hasExpandedDatasets: (state, getters) => {
    return getters.expandedDatasets.length > 0
  },
  isExpandingSome: (state) => {
    return Object.values(state.datasets).some(
      (dataset) => dataset.expansion && dataset.expansion.isExpanding,
    )
  },
}

const actions = {
  reset({ commit }) {
    commit('RESET')
  },
  addDatasets({ commit }, { collectionId, datasets }) {
    const _datasets = {}
    datasets.forEach((dataset) => {
      const datasetId = dataset.id || `d${uuidv4()}`
      _datasets[datasetId] = { collectionId, ...dataset }
    })
    console.debug('Adding datasets:', _datasets)
    commit('ADD_DATASETS', _datasets)
  },
  renameDataset({ commit }, payload) {
    commit('RENAME_DATASET', payload)
  },
  removeAllDatasets({ commit }) {
    commit('REMOVE_ALL_DATASETS')
  },
  cleanupDatasets({ commit, getters }) {
    getters.obsoleteDatasetIds.forEach((id) => commit('REMOVE_DATASET', id))
  },
  // Expansion actions
  async expandDataset({ commit, state, rootState }, { datasetId, pattern }) {
    const dataset = state.datasets[datasetId]
    if (!dataset || !dataset.expansion || !dataset.expansion.expandable) {
      return
    }

    const extension = normalizeExtensionPattern(pattern)
    const dropbox = rootState.dataloader.dropbox.data

    commit('SET_EXPANSION_STATE', {
      datasetId,
      expansionData: { isExpanding: true, expandPattern: pattern },
    })

    try {
      const expandedFiles = await getDropboxFolderContent(
        dropbox,
        { ...dataset, relativePath: dataset.expansion.relativePath },
        { recursive: true, extension },
      )

      commit('EXPAND_DATASET', { datasetId, expandedFiles })

      // Swap files in dropbox
      commit(
        'dataloader/dropbox/removeMultipleFiles',
        dataset.expansion.originalFiles.map((f) => f.id),
        { root: true },
      )
      commit('dataloader/dropbox/addSelectedFiles', expandedFiles, {
        root: true,
      })
    } catch (error) {
      commit('SET_EXPANSION_STATE', {
        datasetId,
        expansionData: { isExpanding: false },
      })
      commit(
        'notify',
        {
          type: 'error',
          message:
            error.response?.data?.message || 'Failed to expand dataset folder',
        },
        { root: true },
      )
      throw error
    }
  },
  async collapseDataset({ commit, state }, datasetId) {
    const dataset = state.datasets[datasetId]
    if (!dataset || !dataset.expansion || !dataset.expansion.isExpanded) {
      return
    }

    const originalFiles = dataset.expansion.originalFiles
    const currentFiles = dataset.files

    commit('COLLAPSE_DATASET', { datasetId })

    // Swap files in dropbox: add back original, remove expanded
    commit(
      'dataloader/dropbox/removeMultipleFiles',
      currentFiles.map((f) => f.id),
      { root: true },
    )
    commit('dataloader/dropbox/addSelectedFiles', originalFiles, { root: true })
  },
  async expandAllDatasets({ dispatch, getters }, pattern) {
    const expandableIds = getters.expandableDatasets
    const promises = expandableIds.map((id) =>
      dispatch('expandDataset', { datasetId: id, pattern }),
    )
    await Promise.all(promises)
  },
  async collapseAllDatasets({ dispatch, getters }) {
    const expandedIds = getters.expandedDatasets
    for (const id of expandedIds) {
      await dispatch('collapseDataset', id)
    }
  },
  async removeDataset({ dispatch, state, commit }, datasetId) {
    const dataset = state.datasets[datasetId]

    // Auto-collapse if expanded before removing
    if (dataset && dataset.expansion && dataset.expansion.isExpanded) {
      await dispatch('collapseDataset', datasetId)
    }

    commit('REMOVE_DATASET', datasetId)
  },
  async removeCollectionDatasets({ dispatch, state, commit }, collectionId) {
    // Get all datasets in this collection
    const datasetsInCollection = Object.keys(state.datasets).filter(
      (id) => state.datasets[id].collectionId === collectionId,
    )

    // Collapse all expanded datasets first
    for (const datasetId of datasetsInCollection) {
      const dataset = state.datasets[datasetId]
      if (dataset.expansion && dataset.expansion.isExpanded) {
        await dispatch('collapseDataset', datasetId)
      }
    }

    // Remove all datasets
    commit('REMOVE_DATASETS_BY_IDS', datasetsInCollection)
  },
}

const mutations = {
  RESET(state) {
    Object.assign(state, { datasets: {} })
  },
  ADD_DATASETS(state, payload) {
    Object.entries(payload).forEach(
      ([id, { collectionId, name, files, expansion }]) => {
        // If expansion state exists (from update), preserve it; otherwise create new
        const expansionState = expansion || createExpansionState(files)

        state.datasets = {
          ...state.datasets,
          [id]: { collectionId, name, files, expansion: expansionState },
        }
      },
    )
  },
  RENAME_DATASET(state, { id, name }) {
    state.datasets[id] = { ...state.datasets[id], name }
  },
  REMOVE_DATASET(state, id) {
    delete state.datasets[id]
  },
  REMOVE_DATASETS_BY_IDS(state, ids) {
    const rmIds = new Set(ids)
    const keepIds = Object.keys(state.datasets).filter((id) => !rmIds.has(id))
    state.datasets = keepIds.reduce((datasets, dId) => {
      datasets = { ...datasets, [dId]: state.datasets[dId] }
      return datasets
    }, {})
  },
  REMOVE_ALL_DATASETS() {
    state.datasets = {}
  },
  // Expansion mutations
  SET_EXPANSION_STATE(state, { datasetId, expansionData }) {
    const dataset = state.datasets[datasetId]
    if (dataset && dataset.expansion) {
      dataset.expansion = {
        ...dataset.expansion,
        ...expansionData,
      }
    }
  },
  EXPAND_DATASET(state, { datasetId, expandedFiles }) {
    const dataset = state.datasets[datasetId]
    if (dataset && dataset.expansion) {
      // Update files with expanded list
      dataset.files = expandedFiles
      // Update expansion state
      dataset.expansion.isExpanded = true
      dataset.expansion.isExpanding = false
      // Mark as no longer expandable (it's now expanded)
      dataset.expansion.expandable = false
    }
  },
  COLLAPSE_DATASET(state, { datasetId }) {
    const dataset = state.datasets[datasetId]
    if (dataset && dataset.expansion) {
      // Restore original files
      dataset.files = dataset.expansion.originalFiles
      // Reset expansion state
      dataset.expansion.isExpanded = false
      dataset.expansion.expandPattern = ''
      // Mark as expandable again
      const isExpandableFolder =
        dataset.expansion.originalFiles.length === 1 &&
        dataset.expansion.originalFiles[0]?.is_dir
      dataset.expansion.expandable = isExpandableFolder
    }
  },
}

const datasets = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
}

export default datasets
